import { ref } from 'vue';

// ─── Opening a report in the DEFAULT browser ───────────────────────────────
// The dashboard is viewed in Chrome, but every report's Interested/Maybe/Pass
// marks live in localStorage — which is per-browser — and all of those are in
// Edge, the default. A plain <a> would open the report in Chrome and strand
// any new marks in a second store that nothing else ever reads.
//
// A web page can't choose a browser. The local report server can, so it does:
// POST the URL to /api/open and it hands the file to the Windows shell.
//
// This works from the deployed https dashboard too. http://localhost is a
// potentially trustworthy origin under Secure Contexts, so mixed-content
// blocking never applies to it; the real gate is Private Network Access, and
// the server answers that preflight for allow-listed origins. Chrome may still
// ask the user to permit local network access the first time. If the call
// fails for any reason — server stopped, permission declined — fall back to
// opening here rather than doing nothing, and say plainly that marks made in
// this browser won't join the ones in Edge.
//
// Extracted from AIReportsView.vue (2026-08-19) when LibraryView became a
// second consumer — one fetch/fallback implementation, not two that can drift.
export const REPORT_SERVER = 'http://localhost:8899';

export function useReportOpener() {
  const openSnack = ref({ show: false, text: '', color: 'success' });

  async function openReport(url) {
    if (!url) return;

    // Two genuinely different failure shapes, previously collapsed into one
    // "couldn't reach the server" message regardless of which happened:
    //   1. The fetch itself throws — real network/CORS failure, the server
    //      is unreachable from this origin at all.
    //   2. The fetch succeeds but the server says no (400/500) — reachable,
    //      but refused this specific file (already gone from disk, outside
    //      the served roots, os.startfile failed, etc.). That's a different
    //      problem with a different fix, and the server's own error message
    //      already says exactly what happened — showing it beats a generic
    //      "check the server is running" when the server plainly is.
    let res;
    try {
      res = await fetch(`${REPORT_SERVER}/api/open`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });
    } catch {
      window.open(url, '_blank', 'noopener');
      openSnack.value = {
        show: true,
        color: 'warning',
        text: "Couldn't reach the report server — opened in this browser instead. "
          + 'Ratings saved here will not join the ones in Edge. '
          + 'Check the server is running, or allow local network access if Chrome asked.',
      };
      return;
    }

    if (!res.ok) {
      const { error } = await res.json().catch(() => ({}));
      window.open(url, '_blank', 'noopener');
      openSnack.value = {
        show: true,
        color: 'warning',
        text: `The report server couldn't open that: ${error || `server returned ${res.status}`} `
          + 'Opened in this browser instead — ratings saved here will not join the ones in Edge.',
      };
      return;
    }

    openSnack.value = { show: true, text: 'Opened in your default browser (Edge).', color: 'success' };
  }

  return { openSnack, openReport };
}
